import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  CopyButton,
  Container,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconLink } from '@tabler/icons-react';
import QRCode from 'qrcode';
import { PortalHero } from '../components/PortalHero';
import { tokens } from '../ui/tokens';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  cancelPublicReservation,
  fetchPublicReservation,
  type PublicReservationView,
} from '../api-client';
import { useVenueProfile } from '../profile-context';
import { JumpingStickmanGame } from '../components/JumpingStickmanGame';

const STATUS_ZH: Record<string, string> = {
  pending: '已送出，等候確認',
  confirmed: '已確認',
  checked_in: '已報到',
  completed: '已完成',
  canceled: '已取消',
  no_show: '未到',
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'yellow',
  confirmed: 'blue',
  checked_in: 'teal',
  completed: 'green',
  canceled: 'gray',
  no_show: 'red',
};

const PAYMENT_ZH: Record<string, string> = {
  unpaid: '未付款',
  partial: '部分付款',
  paid: '已付清',
  refunded: '已退款',
};

export function ReservationStatusPage(): JSX.Element {
  const profile = useVenueProfile();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('new') === '1';
  const [view, setView] = useState<PublicReservationView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Render the QR once the page mounts — points at the current URL so scanning
  // from another device lands on this exact status page (same token). Silent
  // failure if canvas unavailable (SSR / no DOM); UX still shows the copy link.
  useEffect(() => {
    if (!qrCanvasRef.current) return;
    QRCode.toCanvas(
      qrCanvasRef.current,
      typeof window !== 'undefined' ? window.location.href : '',
      { width: 180, margin: 1, color: { dark: tokens.color.qrDark, light: tokens.color.qrLight } },
      (err) => {
        if (err) console.warn('[portal] QR render failed', err);
      },
    );
  }, [token, view]);

  async function load(silent = false): Promise<void> {
    if (!token) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const v = await fetchPublicReservation(token);
      setView(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Poll every 30s so the guest sees status flips (confirmed / checked_in)
    // without manually refreshing. Pauses when the tab is hidden.
    const iv = setInterval(() => {
      if (!document.hidden) void load(true);
    }, 30_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function cancel(): Promise<void> {
    if (!token) return;
    if (!window.confirm('確定要取消這筆訂位嗎？')) return;
    setCancelling(true);
    setError(null);
    try {
      await cancelPublicReservation(token, 'guest_self_cancel');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (error || !view) {
    return (
      <Container size="sm" py={{ base: 'md', sm: 'xl' }} px={{ base: 'sm', sm: 'md' }}>
        <Alert color="red" title="找不到訂位">
          {error ?? '請確認網址是否正確。'}
        </Alert>
      </Container>
    );
  }

  const start = dayjs(view.startTime).tz(profile.timezone);
  const end = dayjs(view.endTime).tz(profile.timezone);
  const canCancel = view.status === 'pending' || view.status === 'confirmed';

  const bookingTerm = profile.branding.bookingTerm ?? '訂位';

  return (
    <Container size="sm" py={{ base: 'md', sm: 'xl' }} px={{ base: 'sm', sm: 'md' }}>
      <Stack gap="lg">
        {isNew && (
          <>
            <Alert color="teal" title="我們收到了 — 等你來爆汗" radius="lg">
              {bookingTerm}已登記。需要確認的話我們會從 LINE / 電話聯繫。記得保存此頁網址。
            </Alert>
            {/* 預熱小遊戲 — 跳格子呼應店內玩法，等確認通知的空檔試玩 */}
            <JumpingStickmanGame partySize={view.partySize} />
          </>
        )}

        <PortalHero subline={`${bookingTerm}狀態`} />

        <Card p="lg" radius="lg" withBorder>
          <Stack>
            <Group justify="space-between">
              <Text fw={600} size="lg">
                {start.format('YYYY-MM-DD (ddd)')}
              </Text>
              <Badge color={STATUS_COLOR[view.status] ?? 'gray'} size="lg">
                {STATUS_ZH[view.status] ?? view.status}
              </Badge>
            </Group>
            <Text size="sm">
              時間：{start.format('HH:mm')} – {end.format('HH:mm')}
            </Text>
            <Text size="sm">姓名：{view.displayName}</Text>
            <Text size="sm">人數：{view.partySize} 位</Text>

            {view.totalAmount !== null && (
              <>
                <Divider my="xs" />
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    金額：
                  </Text>
                  <Text size="sm" fw={500}>
                    NT${view.totalAmount.toLocaleString('en-US')}
                  </Text>
                  <Badge size="sm" variant="light" color={view.paymentStatus === 'paid' ? 'green' : 'gray'}>
                    {PAYMENT_ZH[view.paymentStatus] ?? view.paymentStatus}
                  </Badge>
                </Group>
                {view.paymentStatus !== 'paid' &&
                  view.status !== 'canceled' &&
                  profile.branding.paymentInstructions && (
                    <Alert color="yellow" radius="md" mt={4} variant="light">
                      <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                        {profile.branding.paymentInstructions}
                      </Text>
                    </Alert>
                  )}
              </>
            )}

            <Divider my="xs" />
            <Stack gap={4} align="center">
              <canvas ref={qrCanvasRef} style={{ borderRadius: 8 }} />
              <Text size="xs" c="dimmed" ta="center">
                手機掃 QR 保存；或複製連結傳給自己
              </Text>
              <CopyButton value={typeof window !== 'undefined' ? window.location.href : ''}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? '已複製' : '複製此頁連結'}>
                    <Button
                      size="xs"
                      variant="subtle"
                      leftSection={copied ? <IconCheck size={14} /> : <IconLink size={14} />}
                      onClick={copy}
                    >
                      {copied ? '已複製連結' : '複製連結'}
                    </Button>
                  </Tooltip>
                )}
              </CopyButton>
            </Stack>

            {canCancel && (
              <Button color="red" variant="light" onClick={() => void cancel()} loading={cancelling}>
                取消此{profile.branding.bookingTerm ?? '訂位'}
              </Button>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
